// src/pages/Newsletter/NewsletterVerifyPage.tsx
import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail, ArrowLeft } from 'lucide-react';
import { config } from '../../config';

const API_BASE_URL = config.apiBaseUrl;

const NewsletterVerifyPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  const subscriptionId = searchParams.get('id');

  useEffect(() => {
    const verifySubscription = async () => {
      if (!subscriptionId) {
        setStatus('error');
        setMessage('Invalid verification link. Missing subscription ID.');
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/email-notifications/newsletter/verify/${subscriptionId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        
        if (response.ok) {
          setStatus('success');
          setMessage('Your newsletter subscription has been verified successfully!');
        } else {
          const data = await response.json().catch(() => ({}));
          setStatus('error');
          setMessage(data.message || 'Failed to verify subscription. The link may have expired or already been used.');
        }
      } catch (error) {
        console.error('Newsletter verification error:', error);
        setStatus('error');
        setMessage(`An error occurred while verifying your subscription: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };

    verifySubscription();
  }, [subscriptionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Back to Home */}
        <Link
          to="/"
          className="inline-flex items-center text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 text-center">
          {/* Icon */}
          <div className="mb-6">
            {status === 'loading' && (
              <div className="w-16 h-16 mx-auto bg-blue-500/20 rounded-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            )}
            {status === 'error' && (
              <div className="w-16 h-16 mx-auto bg-red-500/20 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            )}
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-2">
            {status === 'loading' && 'Verifying Subscription...'}
            {status === 'success' && 'Subscription Verified!'}
            {status === 'error' && 'Verification Failed'}
          </h1>

          {/* Message */}
          <p className="text-gray-400 mb-6">
            {status === 'loading' && 'Please wait while we verify your newsletter subscription.'}
            {(status === 'success' || status === 'error') && message}
          </p>

          {/* Success Content */}
          {status === 'success' && (
            <div className="space-y-4">
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                <div className="flex items-center justify-center gap-2 text-green-400">
                  <Mail className="w-5 h-5" />
                  <span>You're now subscribed to our newsletter!</span>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                You'll receive updates about new content, features, and exclusive offers.
              </p>
            </div>
          )}

          {/* Error Content */}
          {status === 'error' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                If you continue to have issues, please try subscribing again or contact our support team.
              </p>
            </div>
          )}

          {/* Action Button */}
          <div className="mt-8">
            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white font-medium rounded-lg transition-all duration-200 shadow-lg shadow-red-500/25"
            >
              {status === 'success' ? 'Start Exploring' : 'Go to Home'}
            </Link>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          © {new Date().getFullYear()} StrymHub. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default NewsletterVerifyPage;
